/**
 * The sign-in gate's land-cover animation — its frames, and its clock.
 *
 * Client instruction, 2026-09-11: *"honestly no one is interested in what changed and what
 * didn't change, they just want to see that dynamism playing out."* So the gate plays the
 * eight published land-cover epochs (2017–2024) as a dissolve instead of showing the newest
 * one and stopping.
 *
 * Two things live here because they are one decision. The **frames** are committed artwork
 * (`manage.py render_gate_epochs`, whose docstring carries the rendering argument) and the
 * **loop** is the timeline that plays them. `GateMap` builds Web Animations from these
 * functions for both the raster stack *and* the year marker, so the number on screen and the
 * pixels under it are driven by one set of arithmetic and cannot drift apart. That is not
 * decorative tidiness: an odometer that is 200 ms out of step with its own raster reads as a
 * bug, on the one page every visitor sees.
 *
 * ## Why baked frames and not eight live tile layers
 *
 * Every epoch already has its own `tile_template` in `/api/v1/layers`, so eight
 * cross-faded `L.tileLayer`s would cost zero committed bytes. Measured against this
 * deployment, that is **96 tile requests over HTTP/1.1** (nginx on :8090 answers 1.1, so six
 * connections per origin) on the sign-in page — and the gate's all-or-nothing reveal rule
 * becomes eight times more likely to abandon. The eight baked frames are 143 kB in total,
 * one request each, and they cannot half-arrive. The live-layer option stays available if
 * IFDC ever objects to committing image bytes.
 *
 * ## Why the dissolve is CSS/WAAPI opacity and not a baked animation
 *
 * Blending two 8-colour categorical rasters produces 28 unique colours, which is exactly
 * what destroys lossless compression: measured with `img2webp -lossless -m 6`, 2/4/8
 * intermediate blend frames per step cost 3.4× / 5.9× / 10.7× the hold-only file, and even
 * eight steps is a visibly quantised fade. An interpolated `opacity` is continuous, is
 * handled in the compositor, and costs nothing.
 */

/** The sidecar `render_gate_epochs` writes beside the frames. Only the fields read here. */
interface Sidecar {
  bbox_4326: [number, number, number, number];
  width: number;
  height: number;
  densities: number[];
  attribution: string;
  frames: { year: number; files: Record<string, string> }[];
}

export interface EpochFrame {
  year: number;
  /** The 1× URL, hashed into `dist/assets` by Vite. */
  src: string;
  /** `srcset` when the command was run with `--dpr 1 2`; `null` for the 1× set. */
  srcSet: string | null;
}

export interface EpochSet {
  frames: EpochFrame[];
  /** `[[south, west], [north, east]]` — what `L.imageOverlay` takes, and what the gate
   *  checks against the outlines it actually drew. */
  bounds: [[number, number], [number, number]];
  /** The CC-BY credit, naming the epoch range. Baked by the render command. */
  attribution: string;
}

/*
 * Both globs repeat their options inline: Vite resolves `import.meta.glob`'s second argument
 * at transform time, so it cannot be a shared constant (the same argument `gateAssets` makes,
 * for the same reason).
 *
 * The frame glob is `lc-*.webp` and nothing else, because an eager `?url` glob emits every
 * file it matches into `dist/assets` whether the page asks for it or not. The sidecar is
 * imported as *data* rather than as a URL, so it is inlined into the bundle and the gate does
 * not spend a request discovering what it is about to draw.
 *
 * Both are globs rather than static imports for the reason the whole of `gateAssets` is: a
 * missing file must degrade, not fail the build. With no `epochs/` directory this module
 * exports `null`, `GateMap` never mounts the stack, and the page is the approved one —
 * basemap, four filled countries, the gradient under them.
 */
const sidecars = import.meta.glob('../../assets/gate/epochs/epochs.json', {
  eager: true,
  import: 'default',
}) as Record<string, Sidecar>;

const frameUrls = import.meta.glob('../../assets/gate/epochs/lc-*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const byName = new Map(
  Object.entries(frameUrls).map(([path, url]) => [path.split('/').pop()!, url]),
);

/**
 * Fewer than two epochs is not an animation, and a single baked frame is strictly worse than
 * the live layer it replaced — so the gate declines and keeps the approved picture.
 */
export const MIN_FRAMES = 2;

/**
 * The committed frame set, or `null` when it is absent or unusable.
 *
 * Sorted by year here rather than trusted from the file: the playback stacks the oldest on
 * top and fades downward, so the order *is* the animation. A year whose 1× file is missing is
 * dropped rather than faked — the loop is built from whatever survives, which is the same
 * degradation rule the runtime applies to a frame that fails to decode.
 */
export const EPOCHS: EpochSet | null = (() => {
  const sidecar = Object.values(sidecars)[0];
  if (!sidecar?.frames?.length || !sidecar.bbox_4326) return null;
  const frames: EpochFrame[] = [];
  for (const frame of [...sidecar.frames].sort((a, b) => a.year - b.year)) {
    const src = byName.get(frame.files?.['1'] ?? '');
    if (!src) continue;
    const densities = (sidecar.densities ?? [1]).filter((d) => d > 1);
    const extra = densities
      .map((d) => {
        const url = byName.get(frame.files?.[String(d)] ?? '');
        return url ? `${url} ${d}x` : null;
      })
      .filter((entry): entry is string => !!entry);
    frames.push({
      year: frame.year,
      src,
      srcSet: extra.length ? [`${src} 1x`, ...extra].join(', ') : null,
    });
  }
  if (frames.length < MIN_FRAMES) return null;
  const [west, south, east, north] = sidecar.bbox_4326;
  return {
    frames,
    bounds: [
      [south, west],
      [north, east],
    ],
    attribution: sidecar.attribution,
  };
})();

/**
 * The loop, in milliseconds.
 *
 * The shape is **hold on the newest year, rewind to the oldest, walk forward, rest on the
 * newest** — and that direction is doing three jobs at once. The loop starts and ends on the
 * same picture, so it repeats with no rewind artefact. `iterations: 3` with a forwards fill
 * therefore settles on the *newest* epoch rather than the oldest, which honours the gate's
 * existing "after the reveal the DOM is at rest" principle without a line of extra logic. And
 * the opening hold shows the year the still page would have shown anyway, so the handoff from
 * "revealed" to "playing" is invisible. The page reads as *here is today, and here is how we
 * got here*, which is a better story than starting in 2017.
 *
 * ## Why `dissolve` is long and `dwell` is short — the 2026-09-11 rebalance
 *
 * The first cut of this loop was `dwell 2400 / dissolve 900`, i.e. a slideshow with holds. It
 * was measured on the built page and the result failed the client's own ask: sampling the
 * panel every 200 ms across a whole loop — both by stepping `currentTime` and, independently,
 * in real time with no pausing at all — **70.5 % of the loop had literally zero changed
 * pixels**, motion came in eight ~1 s bursts separated by ~2.3 s of a frozen page, and the
 * longest dead stretch was **3.4 s**. A front door that stands still two thirds of the time is
 * not "that dynamism playing out", however correct each individual dissolve is.
 *
 * So the holds are spent on the dissolve instead. The step is the same length to within a few
 * hundred ms, but it is now *mostly fade*: `dwell 500 / dissolve 1900`. The year still flips
 * crisply — the odometer is untouched at 260 ms, and it flips at the *start* of the dissolve,
 * so the number announces the change and the raster then resolves toward it — and the dot and
 * fill now travel for 1900 ms out of every 2400 instead of 900 out of every 3300.
 *
 * This does not make any single year-step more visible; nothing can, and it would be dishonest
 * to claim otherwise (consecutive years differ by 2.63–4.50 % of data pixels, and the brief's
 * "~1 %" is an understatement only because 2017→2024 is 11.76 %). What it buys is that the
 * panel is never *still*: continuity is what reads as alive, and a page that freezes solid for
 * 2.4 s at a time reads as broken rather than slow. Re-measured after the change: motion in
 * **93.5 %** of 200 ms windows, longest dead stretch **1.9 s** (the deliberate rest on 2024).
 *
 * The opening and closing holds are trimmed with it — 900→500 and 2600→1500 — because they are
 * the seam where the two meet and together they were the 3.4 s stretch. 1.5 s is still a beat
 * long enough to read as *and here is today* before the rewind, which is the whole point of
 * starting on the newest year.
 */
export const LOOP = {
  openingHold: 500,
  rewind: 1200,
  dwell: 500,
  dissolve: 1900,
  closingHold: 1500,
  /**
   * How long the year *number* takes to roll, at the head of the raster's dissolve.
   *
   * Short on purpose. Type cannot use the raster's technique — eight numbers stacked in one
   * box and cross-faded render 2020 over 2024 as "2029" — so the number is an odometer, and
   * an odometer that takes 900 ms to travel one line reads as a slider being dragged. At 260
   * ms it reads as a digit flipping, and at most two numbers are ever partly visible.
   *
   * It is deliberately *not* stretched to match the longer dissolve: the number is the one
   * crisp event in a composition that is otherwise all gradual change, and stretching it would
   * spend the only sharp edge the panel has.
   */
  odometerRoll: 260,
} as const;

/** Loops before the panel settles on the newest epoch. ~60 s at eight years. */
export const ITERATIONS = 3;

/**
 * Linear for the raster. The dissolve's whole correctness argument is that the composite is
 * `incoming·a + outgoing·(1−a)` with nothing showing through, and that holds at any easing —
 * but a symmetric ease makes two nearly identical pictures appear to stall at both ends,
 * which is the one thing a 3 % change cannot afford.
 */
const DISSOLVE_EASE = 'linear';

/**
 * The odometer's flip and the rewind are eased; they are short, discrete gestures.
 *
 * The dot and the fill are **not** — see `RAIL_EASE`.
 */
const TRAVEL_EASE = 'cubic-bezier(.4,0,.2,1)';

/**
 * The rail's dot and fill run linearly.
 *
 * They used to share `TRAVEL_EASE`, which was right when the travel was 900 ms: an ease-in-out
 * over a short hop reads as a thing being placed. Over the rebalanced 1900 ms dissolve it reads
 * as a stall at both ends — `cubic-bezier(.4,0,.2,1)` spends its first and last ~400 ms barely
 * moving, so a 500 ms dwell flanked by two of those is ~1.3 s of near-motionless rail in the
 * middle of the fade we lengthened specifically to remove motionless stretches.
 *
 * Linear is also what the thing *is*. The rail is a time axis; time does not ease.
 */
const RAIL_EASE = 'linear';

interface Beats {
  total: number;
  riseStart: number;
  riseEnd: number;
  /** `[start, end]` of each year's dissolve, oldest first. `n − 1` of them. */
  steps: [number, number][];
}

/**
 * The one timeline every animation on this panel is cut from.
 *
 * @param years how many epochs actually loaded — never assumed to be eight.
 */
export function beats(years: number): Beats {
  const { openingHold, rewind, dwell, dissolve, closingHold } = LOOP;
  const riseStart = openingHold;
  const riseEnd = openingHold + rewind;
  const steps: [number, number][] = [];
  for (let i = 0; i < years - 1; i += 1) {
    const start = riseEnd + i * (dwell + dissolve) + dwell;
    steps.push([start, start + dissolve]);
  }
  const last = steps.at(-1)?.[1] ?? riseEnd;
  return { total: last + closingHold, riseStart, riseEnd, steps };
}

/** `loopMs(8)` is 20 000. */
export const loopMs = (years: number) => beats(years).total;

/**
 * One raster layer's opacity over the loop.
 *
 * `index` counts from the **oldest** epoch, which is the one on top of the stack: the layers
 * are painted oldest-over-newest and the timeline runs as fade-*outs*, so the layer
 * underneath the one currently dissolving is always fully opaque and the composite is never
 * less than covered.
 *
 * That is the difference between this looking like a dissolve and looking like a fault. A
 * symmetric cross-fade — outgoing out while incoming in — leaves neither opaque at the
 * midpoint and the composite becomes `B·t + A·(1−t)² + basemap·t·(1−t)`: at t = 0.5 a quarter
 * of the dark filtered basemap shows through the land cover. Browser-verified on this
 * geometry: minimum composite coverage 0.7501 and an 11.6 % luminance dip on *every*
 * transition, against 1.0000 and +0.6 % for the shape below.
 *
 * The newest epoch (`index === years − 1`) is never animated — it is the floor of the stack
 * and stays opaque — so this is only called for `0 … years − 2`.
 */
export function layerKeyframes(index: number, years: number): Keyframe[] {
  const { total, riseStart, riseEnd, steps } = beats(years);
  // `steps` has exactly `years − 1` entries and the caller only animates `years − 1` layers, so
  // the fallback is unreachable; it is here rather than a throw because the alternative to a
  // wrong keyframe on a decorative backdrop is an exception on the sign-in page. A layer that
  // took it would simply never appear.
  const [fallStart, fallEnd] = steps[index] ?? [riseEnd, riseEnd];
  return [
    { offset: 0, opacity: 0, easing: 'linear' },
    { offset: riseStart / total, opacity: 0, easing: TRAVEL_EASE },
    { offset: riseEnd / total, opacity: 1, easing: 'linear' },
    { offset: fallStart / total, opacity: 1, easing: DISSOLVE_EASE },
    { offset: fallEnd / total, opacity: 0, easing: 'linear' },
    { offset: 1, opacity: 0 },
  ];
}

/**
 * The year odometer's strip position.
 *
 * The strip is `years + 1` lines tall and reads **newest, oldest, …, newest** — the newest
 * year twice, once at each end, which is what makes the rewind a single adjacent step instead
 * of a spin through seven numbers nobody asked to see. One line is `100 / (years + 1)` per
 * cent of the strip's own height, so the travel is exact at any font size and the transform
 * is composited.
 */
export function odometerKeyframes(years: number): Keyframe[] {
  const { total, riseStart, riseEnd, steps } = beats(years);
  const line = 100 / (years + 1);
  const at = (position: number) => `translateY(${(-(position * line)).toFixed(6)}%)`;
  const frames: Keyframe[] = [
    { offset: 0, transform: at(0), easing: 'linear' },
    { offset: riseStart / total, transform: at(0), easing: TRAVEL_EASE },
    { offset: riseEnd / total, transform: at(1), easing: 'linear' },
  ];
  steps.forEach(([start], index) => {
    frames.push({ offset: start / total, transform: at(1 + index), easing: TRAVEL_EASE });
    frames.push({
      offset: Math.min(1, (start + LOOP.odometerRoll) / total),
      transform: at(2 + index),
      easing: 'linear',
    });
  });
  frames.push({ offset: 1, transform: at(years) });
  return frames;
}

/**
 * Where along the rail we are, as a fraction — 0 at the oldest tick, 1 at the newest.
 *
 * Used twice with different renderings: the travelling dot (`translateX` of a full-width
 * element whose marker is drawn at its left edge, so a per-cent translate is a per-cent of
 * the rail) and the rail's fill (`scaleX` from the left). Both are transforms, so both are
 * composited, and neither needs the rail's pixel width measured.
 *
 * Unlike the odometer this runs across the **whole** dissolve rather than snapping: the dot
 * moving while the raster fades is what tells a reader that the fade they are watching is a
 * year changing and not a glitch. Since the 2026-09-11 rebalance that dissolve is 1900 ms of
 * every 2400, which makes this the panel's principal continuous motion — so it runs at a
 * constant rate (`RAIL_EASE`) and holds flat only for the 500 ms dwell.
 */
function progress(years: number): { offset: number; value: number; easing: string }[] {
  const { total, riseStart, riseEnd, steps } = beats(years);
  const span = Math.max(1, years - 1);
  const points = [
    { offset: 0, value: 1, easing: 'linear' },
    { offset: riseStart / total, value: 1, easing: TRAVEL_EASE },
    { offset: riseEnd / total, value: 0, easing: RAIL_EASE },
  ];
  steps.forEach(([start, end], index) => {
    points.push({ offset: start / total, value: index / span, easing: RAIL_EASE });
    points.push({ offset: end / total, value: (index + 1) / span, easing: RAIL_EASE });
  });
  points.push({ offset: 1, value: 1, easing: 'linear' });
  return points;
}

export function dotKeyframes(years: number): Keyframe[] {
  return progress(years).map(({ offset, value, easing }) => ({
    offset,
    transform: `translateX(${(value * 100).toFixed(4)}%)`,
    easing,
  }));
}

export function fillKeyframes(years: number): Keyframe[] {
  return progress(years).map(({ offset, value, easing }) => ({
    offset,
    transform: `scaleX(${value.toFixed(6)})`,
    easing,
  }));
}

/**
 * How far the sweep travels, as a per cent of **its own width**. `.gate-sweep` is 55 % of the
 * panel wide and pinned to its left edge, so −115 % parks it clear of the left edge and 200 %
 * parks it clear of the right; the band is off-panel at both ends of every pass, which is what
 * lets opacity snap between passes without anything visibly popping.
 */
const SWEEP_FROM = -115;
const SWEEP_TO = 200;

/**
 * The light sweep — the panel's answer to *"they just want to see that dynamism playing out"*.
 *
 * ## Why this exists
 *
 * The raster cannot carry the client's ask and no amount of tuning the dissolve will change
 * that. Measured on the shipped build at 90 ms sampling — roughly what the eye integrates —
 * the **map region contributes 0.00–0.01 %** of changed pixels per frame through the entire
 * dissolve phase, which is 17 of the loop's 20 seconds. Everything that moved lived inside the
 * year marker: a 362 × 34 chip that is **2.06 %** of the panel, in a corner, above the title.
 * Laid out as a contact sheet, four frames spanning six seconds are indistinguishable except
 * for the digits in that chip. That is a failed client ask, not a subtle one.
 *
 * The cause is not the dissolve's shape, which is correct (composite coverage stays 1.0). It
 * is the data: consecutive epochs differ by 2.6–4.5 % of pixels and 2017→2024 by 4.28 % of the
 * map region at a mean delta of **1.50/255**, scattered as per-pixel speckle rather than as a
 * moving front. A cross-fade between two pictures that are 97 % identical is invisible whatever
 * curve it follows, and so is a wipe: the edge between two identical images is not an edge.
 *
 * So the motion has to be something that is *not* the difference between two frames. This is a
 * soft warm band that crosses the whole panel once per year — during the rewind, then once
 * inside each dissolve — arriving as the odometer rolls. It is synchronised to the year change
 * rather than free-running, so it reads as *this year arriving* rather than as a loading
 * shimmer, which is the one thing a sign-in page must not look like.
 *
 * ## Why it is safe
 *
 * It is `opacity` and `transform` only, so it stays in the compositor with everything else here
 * — no filter, no `mask-position`, nothing that would re-rasterise eight stacked 896 × 684
 * layers every frame. It is a **sibling of the map and earlier in the DOM than
 * `.gate-art-wash`**, both at `z-index:1`, so the wash paints over it and the band is
 * attenuated by exactly the film the copy's contrast was measured against. And it only ever
 * lightens toward the ivory the plates were already designed against — the documented worst
 * case for `.gate-plate` is a hypothetical pure-white raster pixel underneath, and the band
 * cannot push any ground past that. Re-measured after shipping it, not assumed: see the
 * contrast table in `base.css` above `.gate-sweep`.
 *
 * Under `prefers-reduced-motion` no animation is built at all, and `.gate-sweep`'s CSS rest
 * state is `opacity:0` — so the band simply does not exist, rather than resting somewhere.
 */
export function sweepKeyframes(years: number): Keyframe[] {
  const { total, riseStart, riseEnd, steps } = beats(years);
  // The rewind is the loop's single largest real change (5.3 % of the map region against the
  // 2.6–4.5 % of a step), so it gets a pass too — which also stops the rewind reading as a
  // glitch, since a jump backwards through seven years is the one moment the picture visibly
  // lurches.
  const passes: [number, number][] = [[riseStart, riseEnd], ...steps];
  const park = (x: number) => `translate3d(${x}%,0,0)`;
  // One millisecond, expressed as an offset. Used to snap opacity while the band is parked
  // off-panel; a zero-length step would collapse to a single keyframe and blend the passes
  // into one continuous crawl.
  const eps = 1 / total;
  const frames: Keyframe[] = [
    { offset: 0, opacity: 0, transform: park(SWEEP_FROM), easing: 'linear' },
  ];
  for (const [start, end] of passes) {
    const from = start / total;
    const to = end / total;
    frames.push({ offset: Math.max(0, from - eps), opacity: 0, transform: park(SWEEP_FROM), easing: 'linear' });
    frames.push({ offset: from, opacity: 1, transform: park(SWEEP_FROM), easing: 'linear' });
    // Linear, for the reason the rail is linear: an eased sweep spends its ends barely moving,
    // and the ends are where a band this soft is already hardest to see.
    frames.push({ offset: to, opacity: 1, transform: park(SWEEP_TO), easing: 'linear' });
    frames.push({ offset: Math.min(1, to + eps), opacity: 0, transform: park(SWEEP_TO), easing: 'linear' });
  }
  frames.push({ offset: 1, opacity: 0, transform: park(SWEEP_TO) });
  return frames;
}
